import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import {
  PANE_TAB_BAR_VISIBLE,
  PANE_TAB_BAR_TOGGLEABLE,
  PANE_ASSETS_VISIBLE,
  PANE_ASSETS_WIDTH,
  PANE_ASSETS_TOGGLEABLE,
  PANE_TOOLS_VISIBLE,
  PANE_TOOLS_WIDTH,
  PANE_TOOLS_TOGGLEABLE,
  PANE_ASSISTANT_VISIBLE,
  PANE_ASSISTANT_WIDTH,
  PANE_ASSISTANT_HEIGHT,
  PANE_ASSISTANT_TOGGLEABLE,
  PANE_ASSISTANT_FULL_SCREEN,
} from "../config/env";

/**
 * Layout state atoms for workspace panes
 */

export interface PaneState {
  isVisible: boolean;
  width: number;
  height: number;
  minWidth: number;
  minHeight: number;
  maxWidth?: number;
  maxHeight?: number;
}

export interface AssetsSectionHeights {
  files: number;
  party: number;
  minSectionHeight: number;
}

export interface WorkspaceLayout {
  assets: PaneState & { toggleable: boolean };
  tools: PaneState & { toggleable: boolean };
  assistant: PaneState & { toggleable: boolean; fullScreen: boolean };
  tabBar: {
    isVisible: boolean;
    height: number;
    toggleable: boolean;
  };
  assetsSections: AssetsSectionHeights;
  textInput: {
    height: number;
    minHeight: number;
    maxHeight: number;
  };
}

// Default layout configuration using environment variables
const defaultLayout: WorkspaceLayout = {
  assets: {
    isVisible: PANE_ASSETS_VISIBLE,
    width: PANE_ASSETS_WIDTH,
    height: 400,
    minWidth: 100,
    minHeight: 100,
    toggleable: PANE_ASSETS_TOGGLEABLE,
  },
  tools: {
    isVisible: PANE_TOOLS_VISIBLE,
    width: PANE_TOOLS_WIDTH,
    height: 400,
    minWidth: 100,
    minHeight: 100,
    toggleable: PANE_TOOLS_TOGGLEABLE,
  },
  assistant: {
    isVisible: PANE_ASSISTANT_VISIBLE,
    width: PANE_ASSISTANT_WIDTH,
    height: PANE_ASSISTANT_HEIGHT,
    minWidth: 300,
    minHeight: 100,
    toggleable: PANE_ASSISTANT_TOGGLEABLE,
    fullScreen: PANE_ASSISTANT_FULL_SCREEN,
  },
  tabBar: {
    isVisible: PANE_TAB_BAR_VISIBLE,
    height: 40,
    toggleable: PANE_TAB_BAR_TOGGLEABLE,
  },
  assetsSections: {
    files: 200,
    party: 200,
    minSectionHeight: 100,
  },
  textInput: {
    height: 120,
    minHeight: 60,
    maxHeight: 300,
  },
};

// Persistent layout state with environment variable awareness
export const workspaceLayoutAtom = atomWithStorage<WorkspaceLayout>(
  "ive-workspace-layout",
  defaultLayout,
  {
    getItem: (key, initialValue) => {
      try {
        const stored = localStorage.getItem(key);
        if (!stored) return initialValue;

        const storedLayout = JSON.parse(stored) as WorkspaceLayout;

        // Merge stored layout with current environment defaults
        // This ensures environment variable changes take effect
        const mergedLayout: WorkspaceLayout = {
          assets: {
            ...storedLayout.assets,
            // Override visibility and toggleable with current env vars
            isVisible: defaultLayout.assets.isVisible,
            toggleable: defaultLayout.assets.toggleable,
          },
          tools: {
            ...storedLayout.tools,
            // Override visibility and toggleable with current env vars
            isVisible: defaultLayout.tools.isVisible,
            toggleable: defaultLayout.tools.toggleable,
          },
          assistant: {
            ...storedLayout.assistant,
            // Override visibility and toggleable with current env vars
            isVisible: defaultLayout.assistant.isVisible,
            toggleable: defaultLayout.assistant.toggleable,
            fullScreen: defaultLayout.assistant.fullScreen,
          },
          tabBar: {
            ...storedLayout.tabBar,
            // Override visibility and toggleable with current env vars
            isVisible: defaultLayout.tabBar.isVisible,
            toggleable: defaultLayout.tabBar.toggleable,
          },
          assetsSections:
            storedLayout.assetsSections || defaultLayout.assetsSections,
          textInput: storedLayout.textInput || defaultLayout.textInput,
        };

        return mergedLayout;
      } catch {
        return initialValue;
      }
    },
    setItem: (key, value) => {
      localStorage.setItem(key, JSON.stringify(value));
    },
    removeItem: (key) => {
      localStorage.removeItem(key);
    },
  },
);

// Window dimensions atom for responsive layout calculations
export const windowDimensionsAtom = atom({
  width: typeof window !== "undefined" ? window.innerWidth : 1200,
  height: typeof window !== "undefined" ? window.innerHeight : 800,
});

// Action atom to update window dimensions
export const updateWindowDimensionsAtom = atom(
  null,
  (_get, set, dimensions: { width: number; height: number }) => {
    set(windowDimensionsAtom, dimensions);
  },
);

// Action atom to trigger assistant pane repositioning on window resize
export const repositionAssistantPaneAtom = atom(null, (get, set) => {
  const layout = get(workspaceLayoutAtom);

  // Only reposition if assistant is visible and not in full screen mode
  if (!layout.assistant.isVisible || layout.assistant.fullScreen) {
    return;
  }

  // Force a layout recalculation by updating the layout atom
  // This triggers UI components to recalculate positioning
  set(workspaceLayoutAtom, { ...layout });
});

// Individual pane visibility atoms
export const assetsPaneVisibleAtom = atom(
  (get) => get(workspaceLayoutAtom).assets.isVisible,
  (get, set, visible: boolean) => {
    const layout = get(workspaceLayoutAtom);
    set(workspaceLayoutAtom, {
      ...layout,
      assets: { ...layout.assets, isVisible: visible },
    });
  },
);

export const toolsPaneVisibleAtom = atom(
  (get) => get(workspaceLayoutAtom).tools.isVisible,
  (get, set, visible: boolean) => {
    const layout = get(workspaceLayoutAtom);
    set(workspaceLayoutAtom, {
      ...layout,
      tools: { ...layout.tools, isVisible: visible },
    });
  },
);

export const assistantPaneVisibleAtom = atom(
  (get) => get(workspaceLayoutAtom).assistant.isVisible,
  (get, set, visible: boolean) => {
    const layout = get(workspaceLayoutAtom);
    set(workspaceLayoutAtom, {
      ...layout,
      assistant: { ...layout.assistant, isVisible: visible },
    });
  },
);

export const tabBarVisibleAtom = atom(
  (get) => get(workspaceLayoutAtom).tabBar.isVisible,
  (get, set, visible: boolean) => {
    const layout = get(workspaceLayoutAtom);
    set(workspaceLayoutAtom, {
      ...layout,
      tabBar: { ...layout.tabBar, isVisible: visible },
    });
  },
);

// Pane toggleable state atoms (read-only)
export const assetsPaneToggleableAtom = atom(
  (get) => get(workspaceLayoutAtom).assets.toggleable,
);

export const toolsPaneToggleableAtom = atom(
  (get) => get(workspaceLayoutAtom).tools.toggleable,
);

export const assistantPaneToggleableAtom = atom(
  (get) => get(workspaceLayoutAtom).assistant.toggleable,
);

export const tabBarToggleableAtom = atom(
  (get) => get(workspaceLayoutAtom).tabBar.toggleable,
);

// Assistant pane full-screen state atom (read-only)
export const assistantPaneFullScreenAtom = atom(
  (get) => get(workspaceLayoutAtom).assistant.fullScreen,
);

// Pane resize actions
export const updatePaneSizeAtom = atom(
  null,
  (
    get,
    set,
    pane: "assets" | "tools" | "assistant" | "tabBar",
    width: number,
    height: number,
    editorWidth?: number,
  ) => {
    const layout = get(workspaceLayoutAtom);

    if (pane === "tabBar") {
      // Special handling for tab bar
      const constrainedHeight = Math.max(20, Math.min(height, 100));
      set(workspaceLayoutAtom, {
        ...layout,
        tabBar: {
          ...layout.tabBar,
          height: constrainedHeight,
        },
      });
    } else {
      const paneState = layout[pane];

      // Special handling for assistant pane with dynamic max width
      let maxWidth = paneState.maxWidth;
      if (pane === "assistant" && editorWidth) {
        maxWidth = Math.max(paneState.minWidth, editorWidth - 6);
      }

      // Enforce min/max constraints
      const constrainedWidth = Math.max(
        paneState.minWidth,
        maxWidth ? Math.min(width, maxWidth) : width,
      );
      const constrainedHeight = Math.max(
        paneState.minHeight,
        paneState.maxHeight ? Math.min(height, paneState.maxHeight) : height,
      );

      set(workspaceLayoutAtom, {
        ...layout,
        [pane]: {
          ...paneState,
          width: constrainedWidth,
          height: constrainedHeight,
        },
      });
    }
  },
);

// Assets sections height management atoms
export const assetsSectionHeightsAtom = atom(
  (get) => get(workspaceLayoutAtom).assetsSections,
  (get, set, heights: Partial<AssetsSectionHeights>) => {
    const layout = get(workspaceLayoutAtom);
    set(workspaceLayoutAtom, {
      ...layout,
      assetsSections: {
        ...layout.assetsSections,
        ...heights,
      },
    });
  },
);

// Atom to sync actual rendered Assets pane height with stored state
export const syncAssetsPaneHeightAtom = atom(
  null,
  (get, set, actualHeight: number) => {
    const layout = get(workspaceLayoutAtom);
    // Only update if the height has changed significantly (more than 5px difference)
    if (Math.abs(layout.assets.height - actualHeight) > 5) {
      set(workspaceLayoutAtom, {
        ...layout,
        assets: {
          ...layout.assets,
          height: actualHeight,
        },
      });
    }
  },
);

export const updateAssetsSectionHeightAtom = atom(
  null,
  (
    get,
    set,
    section: "files" | "party",
    height: number,
    maxContentHeight?: number,
  ) => {
    const layout = get(workspaceLayoutAtom);
    const { assetsSections, assets } = layout;

    // Calculate available space in assets pane
    // Subtract header height (approx 48px) and bottom margin (5px)
    const availableHeight = assets.height - 48 - 5;

    // Enforce minimum height constraint
    const minHeight = assetsSections.minSectionHeight;
    const constrainedHeight = Math.max(height, minHeight);

    // Calculate the other section's height
    const otherSection = section === "files" ? "party" : "files";
    const otherSectionHeight = assetsSections[otherSection];

    // Ensure both sections fit within available space with minimums
    const totalMinRequired = minHeight * 2;

    if (availableHeight < totalMinRequired) {
      // Not enough space, maintain current proportions but enforce minimums
      return;
    }

    // Calculate maximum allowed height for this section
    // Ensure the other section gets at least its minimum height
    const maxAllowedHeight = availableHeight - minHeight;

    // Apply content height constraint if provided
    let contentConstrainedHeight = constrainedHeight;
    if (maxContentHeight && maxContentHeight > 0) {
      // Check if content height constraint would push other section out of bounds
      const totalWithContent = maxContentHeight + otherSectionHeight;
      const wouldOverflow = totalWithContent > availableHeight;

      // Only apply content constraint if it doesn't cause overflow
      if (!wouldOverflow) {
        contentConstrainedHeight = Math.min(
          constrainedHeight,
          maxContentHeight,
        );
      }
    }

    // Apply maximum constraint to prevent sections from becoming excessively tall
    const finalConstrainedHeight = Math.min(
      contentConstrainedHeight,
      maxAllowedHeight,
    );

    // If the current total would exceed available space, we need to adjust
    if (finalConstrainedHeight + otherSectionHeight > availableHeight) {
      // Adjust the other section to fit remaining space
      const remainingSpace = availableHeight - finalConstrainedHeight;
      const adjustedOtherHeight = Math.max(remainingSpace, minHeight);

      set(workspaceLayoutAtom, {
        ...layout,
        assetsSections: {
          ...assetsSections,
          [section]: finalConstrainedHeight,
          [otherSection]: adjustedOtherHeight,
        },
      });
    } else {
      // Both sections fit comfortably, just update the requested section
      set(workspaceLayoutAtom, {
        ...layout,
        assetsSections: {
          ...assetsSections,
          [section]: finalConstrainedHeight,
        },
      });
    }
  },
);

// Text input height management atoms
export const textInputHeightAtom = atom(
  (get) => get(workspaceLayoutAtom).textInput.height,
);

export const updateTextInputHeightAtom = atom(
  null,
  (get, set, height: number) => {
    const layout = get(workspaceLayoutAtom);
    const { minHeight, maxHeight } = layout.textInput;
    const constrainedHeight = Math.max(minHeight, Math.min(height, maxHeight));

    set(workspaceLayoutAtom, {
      ...layout,
      textInput: {
        ...layout.textInput,
        height: constrainedHeight,
      },
    });
  },
);
